import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, forkJoin, map, of, tap, throwError } from 'rxjs';
import { DoorsWindowsApiService } from './doors-windows-api.service';
import { ProjectService } from './project.service';
import { StepperSessionService } from './stepper-session.service';
import { StepperFloorService } from './stepper-floor.service';
import { AppTitleStrategy } from '@core/title/title.strategy';
import { CATEGORY, LanguageService } from '@core';
import { calculateExternalWallMetrics } from '@core/utils/plumbing-calculator.utils';
import { TranslateService } from '@ngx-translate/core';
import {
  CategoryDetectionResult,
  CroppedImage,
  DetectedFloorData,
  DetectedRoomData,
  DetectionScopeCategory,
  DetectionScopeSaveRequest,
  DoorsWindowsFloorMeasurement,
  MainDocument,
  PointsDataResponse,
  ScaleItem,
  SplitPagesResponse,
  AssignFloorRequest,
  AssignFloorResponse,
} from '@core/models/project.models';

// Re-export types consumed by the rest of the app
export type { StepperProject } from './stepper-session.service';
export type { FloorAssignment, AvailableFloor } from './stepper-floor.service';

/** Display metadata for a project stepper step. */
export interface StepMeta {
  /** One-based step number used for navigation and progress display. */
  number: number;
  /** Label shown in the stepper UI and browser title. */
  label: string;
  /** Translation key for the step label. */
  labelKey: string;
}

const ORDINAL_FLOORS = [1, 2, 3, 4, 5, 6, 7].map((floor) => ({
  id: `${floor}${floor === 1 ? 'st' : floor === 2 ? 'nd' : floor === 3 ? 'rd' : 'th'}`,
  name: `${floor}${floor === 1 ? 'st' : floor === 2 ? 'nd' : floor === 3 ? 'rd' : 'th'} Floor`,
}));

const FLOOR_CANONICAL_ORDER: string[] = [
  'basement',
  'ground floor',
  ...ORDINAL_FLOORS.map(f => f.name.toLowerCase()),
  'rooftop',
];

/**
 * Returns a canonical ordering bucket for known floor names.
 *
 * Unknown names sort after the supported basement/ground/ordinal/rooftop set.
 */
export function floorSortIndex(floorName: string): number {
  const normalized = floorName.toLowerCase().replace(/[_-]+/g, ' ').trim();
  const idx = FLOOR_CANONICAL_ORDER.indexOf(normalized);
  return idx === -1 ? FLOOR_CANONICAL_ORDER.length : idx;
}

/**
 * Coordinates the multi-step project detection workflow.
 *
 * Delegates session persistence to StepperSessionService and floor-assignment
 * state to StepperFloorService; owns detection scope, documents, scale, and
 * results state directly.
 */
@Injectable({ providedIn: 'root' })
export class ProjectStepperService {
  private readonly projectService  = inject(ProjectService);
  private readonly doorsWindowsApi = inject(DoorsWindowsApiService);
  private readonly titleStrategy   = inject(AppTitleStrategy);
  private readonly router          = inject(Router);
  private readonly sessionService  = inject(StepperSessionService);
  private readonly floorService    = inject(StepperFloorService);
  private readonly translate       = inject(TranslateService);
  private readonly lang            = inject(LanguageService);

  constructor() {
    effect(() => {
      if (!this.isOpen()) return;
      const project   = this.project();
      const stepLabel = this.stepLabel(this.currentStep());
      if (project && stepLabel) {
        this.titleStrategy.setPageTitle(`${stepLabel} | ${project.projectName}`);
      }
    });

    effect(() => {
      const project = this.project();
      if (!this.isOpen() || !project) return;
      this.sessionService.save({
        project,
        roomMaterialSelections: this.roomMaterialSelections(),
        roomMaterialRates:      this.roomMaterialRates(),
        roomMaterialQuantities: this.roomMaterialQuantities(),
        contractorComments:     this.contractorComments(),
      });
    });
  }

  // ── Own state ────────────────────────────────────────────────────────────────

  readonly isOpen             = signal(false);
  readonly project            = signal<import('./stepper-session.service').StepperProject | null>(null);
  readonly currentStep        = signal(1);
  readonly maxDetectionScopeCategories = 3;
  readonly selectedCategories = signal<string[]>([]);
  readonly detectionScopeCategories = signal<DetectionScopeCategory[]>([
    { id: CATEGORY.FLOORING.id,       label: CATEGORY.FLOORING.name,       description: CATEGORY.FLOORING.description },
    { id: CATEGORY.DRYWALL.id,        label: CATEGORY.DRYWALL.name,        description: CATEGORY.DRYWALL.description },
    { id: CATEGORY.FIXTURES.id,       label: CATEGORY.FIXTURES.name,       description: CATEGORY.FIXTURES.description },
    { id: CATEGORY.DOORS_WINDOWS.id,  label: CATEGORY.DOORS_WINDOWS.name,  description: CATEGORY.DOORS_WINDOWS.description },
    { id: CATEGORY.INTERIOR_PAINT.id, label: CATEGORY.INTERIOR_PAINT.name, description: CATEGORY.INTERIOR_PAINT.description },
    { id: CATEGORY.HVAC.id,           label: CATEGORY.HVAC.name,           description: CATEGORY.HVAC.description },
    { id: CATEGORY.COLUMNS.id,        label: CATEGORY.COLUMNS.name,        description: CATEGORY.COLUMNS.description },
    { id: CATEGORY.STEEL_BEAMS.id,    label: CATEGORY.STEEL_BEAMS.name,    description: CATEGORY.STEEL_BEAMS.description },
  ]);
  readonly detectionScopeLoading = signal(false);
  readonly detectionScopeSaving  = signal(false);
  readonly detectionScopeError   = signal<string | null>(null);
  readonly uploadedFile          = signal<File | null>(null);
  readonly uploadComplete        = signal(false);
  readonly existingDocuments     = signal<MainDocument[]>([]);
  readonly documentsLoading      = signal(false);
  readonly documentsLoaded       = signal(false);
  readonly scaleList             = signal<ScaleItem[]>([]);
  readonly selectedScale         = signal<ScaleItem | null>(null);
  readonly scalesLoading         = signal(false);
  readonly submittingPage        = signal(false);
  readonly splitPagesLoading     = signal(false);
  readonly hydrationLoading      = signal(false);
  readonly categoryResults       = signal<CategoryDetectionResult[]>([]);
  readonly detectionTriggered    = signal(false);
  readonly step4EditDirty        = signal(false);
  readonly pendingScaleChange    = signal(false);
  readonly flooringPreProcessComplete  = signal(false);
  readonly categoryPreProcessComplete  = signal<Record<string, boolean>>({});
  readonly newDocumentUploaded         = signal(false);
  readonly roomMaterialSelections      = signal<Record<string, string>>({});
  readonly roomMaterialRates           = signal<Record<string, number>>({});
  readonly roomMaterialQuantities      = signal<Record<string, number>>({});
  readonly contractorComments          = signal('');
  readonly doorsWindowsResults         = signal<DoorsWindowsFloorMeasurement[] | null>(null);

  // ── Floor state pass-throughs (backed by StepperFloorService) ────────────────

  readonly floorAssignments        = this.floorService.floorAssignments;
  readonly submittedPageNumbers    = this.floorService.submittedPageNumbers;
  readonly assignedFloorsLoading   = this.floorService.assignedFloorsLoading;
  readonly deletingFloorAssignment = this.floorService.deletingFloorAssignment;
  readonly editingFloorAssignment  = this.floorService.editingFloorAssignment;
  readonly step3DataLoaded         = this.floorService.step3DataLoaded;

  // ── Derived ──────────────────────────────────────────────────────────────────

  readonly fileTypeId = computed(() => Number(this.selectedCategories()[0] ?? '1'));

  readonly steps: StepMeta[] = [
    { number: 1, label: 'Detection Scope', labelKey: 'PROJECT_STEPPER.STEPS.DETECTION_SCOPE' },
    { number: 2, label: 'Upload Plan',     labelKey: 'PROJECT_STEPPER.STEPS.UPLOAD_PLAN' },
    { number: 3, label: 'Assign Floors',   labelKey: 'PROJECT_STEPPER.STEPS.ASSIGN_FLOORS' },
    { number: 4, label: 'Results',         labelKey: 'PROJECT_STEPPER.STEPS.RESULTS' },
  ];

  readonly availableFloors: import('./stepper-floor.service').AvailableFloor[] = [
    { id: 'basement', name: 'Basement'     },
    { id: 'ground',   name: 'Ground Floor' },
    ...ORDINAL_FLOORS,
    { id: 'rooftop',  name: 'Rooftop'      },
  ];

  stepLabel(stepNumber: number): string {
    this.lang.currentLang();
    const step = this.steps.find(s => s.number === stepNumber);
    return step ? this.translate.instant(step.labelKey) : '';
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────────

  /**
   * Restores the last open stepper session from session storage when possible.
   */
  tryRestoreSession(): void {
    const session = this.sessionService.load();
    if (!session) return;
    this.open(session.project);
    this.roomMaterialSelections.set(session.roomMaterialSelections  ?? {});
    this.roomMaterialRates.set(session.roomMaterialRates            ?? {});
    this.roomMaterialQuantities.set(session.roomMaterialQuantities  ?? {});
    this.contractorComments.set(session.contractorComments          ?? '');
  }

  /**
   * Starts a fresh stepper session for a project and loads its detection scope.
   */
  open(project: import('./stepper-session.service').StepperProject): void {
    this.resetState();
    this.project.set(project);
    this.detectionScopeLoading.set(true);
    this.isOpen.set(true);
    this.loadDetectionScope();
  }

  /**
   * Closes the stepper, clears persisted draft state, and restores the route title.
   */
  close(): void {
    this.resetState();
    this.sessionService.clear();
    this.titleStrategy.updateTitle(this.router.routerState.snapshot);
  }

  private resetState(): void {
    this.isOpen.set(false);
    this.project.set(null);
    this.currentStep.set(1);
    this.selectedCategories.set([]);
    this.detectionScopeCategories.set([]);
    this.detectionScopeLoading.set(false);
    this.detectionScopeSaving.set(false);
    this.detectionScopeError.set(null);
    this.uploadedFile.set(null);
    this.uploadComplete.set(false);
    this.existingDocuments.set([]);
    this.documentsLoading.set(false);
    this.documentsLoaded.set(false);
    this.scaleList.set([]);
    this.selectedScale.set(null);
    this.submittingPage.set(false);
    this.splitPagesLoading.set(false);
    this.categoryResults.set([]);
    this.detectionTriggered.set(false);
    this.flooringPreProcessComplete.set(false);
    this.newDocumentUploaded.set(false);
    this.step4EditDirty.set(false);
    this.pendingScaleChange.set(false);
    this.roomMaterialSelections.set({});
    this.roomMaterialRates.set({});
    this.roomMaterialQuantities.set({});
    this.contractorComments.set('');
    this.doorsWindowsResults.set(null);
    this.categoryPreProcessComplete.set({});
    this.floorService.reset();
  }

  // ── Navigation ────────────────────────────────────────────────────────────────

  next(): void {
    if (this.currentStep() < this.steps.length) this.currentStep.update(s => s + 1);
  }

  prev(): void {
    if (this.currentStep() > 1) this.currentStep.update(s => s - 1);
  }

  // ── Detection scope ───────────────────────────────────────────────────────────

  toggleCategory(id: string): void {
    this.selectedCategories.update(cats => {
      if (cats.includes(id)) {
        this.detectionScopeError.set(null);
        return cats.filter(c => c !== id);
      }
      if (cats.length >= this.maxDetectionScopeCategories) {
        this.detectionScopeError.set(this.translate.instant('PROJECT_STEPPER.ERRORS.MAX_CATEGORIES', {
          max: this.maxDetectionScopeCategories,
        }));
        return cats;
      }
      this.detectionScopeError.set(null);
      return [...cats, id];
    });
  }

  loadDetectionScope(): void {
    const projectId = this.project()?.projectId;
    if (!projectId) return;

    this.detectionScopeLoading.set(true);
    this.detectionScopeError.set(null);

    this.projectService.getDetectionScope(projectId).pipe(
      finalize(() => this.detectionScopeLoading.set(false)),
    ).subscribe({
      next: response => {
        this.detectionScopeCategories.set(this.normalizeDetectionScopeCategories(response.data.available_categories));
        this.selectedCategories.set(
          this.normalizeDetectionScopeSelection(response.data.selected_categories)
            .slice(0, this.maxDetectionScopeCategories),
        );
        this.tryHydrateFromServer();
      },
      error: () => {
        this.detectionScopeError.set(this.translate.instant('PROJECT_STEPPER.ERRORS.LOAD_SCOPE'));
      },
    });
  }

  saveDetectionScope() {
    const projectId = this.project()?.projectId;
    if (!projectId) return throwError(() => new Error(this.translate.instant('PROJECT_STEPPER.ERRORS.NO_PROJECT')));

    if (this.selectedCategories().length > this.maxDetectionScopeCategories) {
      this.detectionScopeError.set(this.translate.instant('PROJECT_STEPPER.ERRORS.MAX_CATEGORIES', {
        max: this.maxDetectionScopeCategories,
      }));
      return throwError(() => new Error(this.translate.instant('PROJECT_STEPPER.ERRORS.TOO_MANY_CATEGORIES')));
    }

    const payload: DetectionScopeSaveRequest = { selected_categories: this.selectedCategories() };

    this.detectionScopeSaving.set(true);
    this.detectionScopeError.set(null);

    return this.projectService.saveDetectionScope(projectId, payload).pipe(
      tap(response => {
        this.selectedCategories.set(
          this.normalizeDetectionScopeSelection(response.data.selected_categories)
            .slice(0, this.maxDetectionScopeCategories),
        );
      }),
      catchError(error => {
        this.detectionScopeError.set(this.translate.instant('PROJECT_STEPPER.ERRORS.SAVE_SCOPE'));
        return throwError(() => error);
      }),
      finalize(() => this.detectionScopeSaving.set(false)),
    );
  }

  private normalizeDetectionScopeCategories(categories: DetectionScopeCategory[]): DetectionScopeCategory[] {
    return categories.map(c => ({ ...c, id: String(c.id) }));
  }

  private normalizeDetectionScopeSelection(selected: string[]): string[] {
    return selected.map(id => String(id));
  }

  // ── Documents & scale ─────────────────────────────────────────────────────────

  loadExistingDocuments(force = false): void {
    const projectId = this.project()?.projectId;
    if (!projectId) return;
    if (!force && this.documentsLoaded()) return;

    this.documentsLoaded.set(true);
    this.documentsLoading.set(true);
    this.projectService.getProjectDocuments(projectId).pipe(
      finalize(() => this.documentsLoading.set(false)),
    ).subscribe({
      next: response => {
        this.existingDocuments.set(response.data.documents.main);
        this.hydrateSelectedScale(response.data.selectedScale);
      },
      error: () => {
        this.documentsLoaded.set(false);
        this.existingDocuments.set([]);
      },
    });
  }

  loadScales(): void {
    this.scalesLoading.set(true);
    this.projectService.getScales().pipe(
      finalize(() => this.scalesLoading.set(false)),
    ).subscribe({
      next: response => {
        const scales = response.data.scales ?? [];
        this.scaleList.set(scales);
        const current = this.selectedScale();
        if (current && current.id === 0) {
          const match = scales.find(s => s.scale === current.scale);
          if (match) this.selectedScale.set(match);
        }
      },
      error: () => this.scaleList.set([]),
    });
  }

  saveScale(scale: ScaleItem): void {
    const projectId = this.project()?.projectId;
    if (!projectId) return;
    this.selectedScale.set(scale);
    this.projectService.addScale(projectId, scale.scale, this.fileTypeId()).subscribe({
      error: () => { /* scale already set in UI; silent fail is acceptable */ },
    });
  }

  private hydrateSelectedScale(scaleStr: string | null | undefined, overwrite = true): void {
    const scale = scaleStr?.trim();
    if (!scale || (!overwrite && this.selectedScale())) return;
    const match = this.scaleList().find(s => s.scale === scale);
    this.selectedScale.set(match ?? { id: 0, scale });
  }

  /**
   * Returns the scale string for a given floor, falling back to the global
   * selectedScale when the floor has no stored scaleAssigned.
   */
  scaleForFloor(floorName: string | null | undefined): string | undefined {
    if (floorName) {
      const normalize = (s: string) =>
        s.replace(/[_-]+/g, ' ')
          .replace(/\bpage\s*\d+\b/gi, '')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
      const target = normalize(floorName);
      const assignment = this.floorAssignments().find(a => normalize(a.floorName) === target);
      if (assignment?.scaleAssigned) return assignment.scaleAssigned;
    }
    return this.selectedScale()?.scale;
  }

  // ── Pages & floor assignment ──────────────────────────────────────────────────

  splitPages() {
    const projectId = this.project()?.projectId;
    if (!projectId) return throwError(() => new Error('No project selected.'));

    this.splitPagesLoading.set(true);
    return this.projectService.splitPages(projectId).pipe(
      map((response): SplitPagesResponse => response.data),
      catchError(err => throwError(() => err)),
      finalize(() => this.splitPagesLoading.set(false)),
    );
  }

  submitPage(
    pageNumber: number,
    floorName: string,
    scaleAssigned?: string,
    scaleType?: 'standard' | 'calibrated',
  ) {
    const projectId = this.project()?.projectId;
    if (!projectId) return throwError(() => new Error('No project selected.'));

    this.submittingPage.set(true);

    const payload: AssignFloorRequest = {
      project_id:         projectId,
      page_no:            pageNumber,
      assigned_floor_name: floorName,
      file_type_id:       this.fileTypeId(),
      file_type_ids:      this.selectedCategories()
                            .map(id => Number(id))
                            .filter(Number.isFinite),
      selected_categories: this.selectedCategories(),
      scale_type:         scaleType,
      scale_assigned:     scaleAssigned,
    };

    return this.projectService.assignFloor(payload).pipe(
      map(response => {
        const data = response.data as AssignFloorResponse & { assignment_id?: number; id?: number };
        return data.entries?.[0]?.id ?? data.assignment_id ?? data.id ?? null;
      }),
      tap(() => {
        this.floorService.submittedPageNumbers.update(pages =>
          pages.includes(pageNumber) ? pages : [...pages, pageNumber],
        );
        if (scaleAssigned) this.hydrateSelectedScale(scaleAssigned);
      }),
      catchError(err => throwError(() => err)),
      finalize(() => this.submittingPage.set(false)),
    );
  }

  loadAssignedFloors(): void {
    const projectId = this.project()?.projectId;
    if (!projectId) return;
    if (this.floorService.step3DataLoaded()) return;

    // Optimistic lock: flag before the call so back-navigation during the
    // in-flight request doesn't queue a second fetch.
    this.floorService.step3DataLoaded.set(true);
    this.floorService.assignedFloorsLoading.set(true);

    this.projectService.getAssignedFloors(projectId).pipe(
      finalize(() => this.floorService.assignedFloorsLoading.set(false)),
    ).subscribe({
      next: response => {
        const scale = this.floorService.hydrateFromEntries(response.data.assigned_floors);
        this.hydrateSelectedScale(scale, false);
      },
      error: () => this.floorService.step3DataLoaded.set(false),
    });
  }

  /** Adds or replaces a floor assignment in local stepper state. */
  assignFloor(assignment: import('./stepper-floor.service').FloorAssignment): void {
    this.floorService.assign(assignment);
  }

  /** Deletes an assigned floor — server when persisted, local-only when not yet saved. */
  deleteAssignedFloor(pageNumber: number) {
    return this.floorService.delete$(pageNumber);
  }

  /** Updates the floor name for an existing assignment. */
  editAssignedFloor(id: number, pageNumber: number, newFloorName: string) {
    return this.floorService.edit$(id, pageNumber, newFloorName);
  }

  // ── Pre-process state ─────────────────────────────────────────────────────────

  resetPreProcessState(): void {
    this.flooringPreProcessComplete.set(false);
    this.categoryPreProcessComplete.set({});
  }

  markCategoryPreProcessComplete(categoryId: string): void {
    this.categoryPreProcessComplete.update(current => ({ ...current, [categoryId]: true }));
    if (categoryId === CATEGORY.FLOORING.id) this.flooringPreProcessComplete.set(true);
  }

  isCategoryPreProcessComplete(categoryId: string): boolean {
    return this.categoryPreProcessComplete()[categoryId] ?? (
      categoryId === CATEGORY.FLOORING.id && this.flooringPreProcessComplete()
    );
  }

  // ── Material selections ───────────────────────────────────────────────────────

  materialSelectionKey(categoryId: string | null, floorName: string | null, roomName: string): string {
    return `${categoryId ?? ''}_${floorName ?? ''}_${roomName}`;
  }

  getRoomMaterial(categoryId: string | null, floorName: string | null, roomName: string): string {
    return this.roomMaterialSelections()[this.materialSelectionKey(categoryId, floorName, roomName)] ?? '';
  }

  setRoomMaterial(categoryId: string | null, floorName: string | null, roomName: string, type: string): void {
    const key = this.materialSelectionKey(categoryId, floorName, roomName);
    this.roomMaterialSelections.update(current => ({ ...current, [key]: type.trim() }));
  }

  getRoomMaterialRate(categoryId: string | null, floorName: string | null, roomName: string): number | null {
    const value = this.roomMaterialRates()[this.materialSelectionKey(categoryId, floorName, roomName)];
    return Number.isFinite(value) ? value : null;
  }

  setRoomMaterialRate(categoryId: string | null, floorName: string | null, roomName: string, value: string): void {
    const parsed = Number(value);
    const key = this.materialSelectionKey(categoryId, floorName, roomName);
    this.roomMaterialRates.update(current => {
      const next = { ...current };
      if (value.trim() === '' || !Number.isFinite(parsed) || parsed < 0) {
        delete next[key];
      } else {
        next[key] = parsed;
      }
      return next;
    });
  }

  getRoomMaterialQuantity(categoryId: string | null, floorName: string | null, roomName: string): number | null {
    const value = this.roomMaterialQuantities()[this.materialSelectionKey(categoryId, floorName, roomName)];
    return Number.isFinite(value) ? value : null;
  }

  setRoomMaterialQuantity(categoryId: string | null, floorName: string | null, roomName: string, value: string): void {
    const parsed = Number(value);
    const key = this.materialSelectionKey(categoryId, floorName, roomName);
    this.roomMaterialQuantities.update(current => {
      const next = { ...current };
      if (value.trim() === '' || !Number.isFinite(parsed) || parsed <= 0) {
        delete next[key];
      } else {
        next[key] = parsed;
      }
      return next;
    });
  }

  // ── Detection results ─────────────────────────────────────────────────────────

  initCategoryResults(): void {
    const cats = this.detectionScopeCategories()
      .filter(c => this.selectedCategories().includes(c.id));
    this.categoryResults.set(cats.map(c => ({
      categoryId:    c.id,
      categoryLabel: c.label,
      floors:        [],
      status:        'idle' as const,
      progress:      0,
    })));
    this.detectionTriggered.set(true);
  }

  updateCategoryProgress(categoryId: string, progress: number): void {
    this.categoryResults.update(results =>
      results.map(r => r.categoryId === categoryId
        ? { ...r, progress, status: 'processing' as const }
        : r),
    );
  }

  updateCategoryStatus(
    categoryId: string,
    status: CategoryDetectionResult['status'],
    progress: number,
    errorMessage?: string,
  ): void {
    this.categoryResults.update(results =>
      results.map(r => r.categoryId === categoryId
        ? { ...r, status, progress, ...(errorMessage ? { errorMessage } : {}) }
        : r),
    );
  }

  setCategoryComplete(categoryId: string, floors: DetectedFloorData[]): void {
    this.categoryResults.update(results =>
      results.map(r => r.categoryId === categoryId
        ? { ...r, status: 'complete' as const, progress: 100, floors }
        : r),
    );
  }

  propagatePdfUrls(): void {
    const slug = (name: string) => name.toLowerCase().replace(/[_ ]+/g, '_');
    // HVAC, Columns, and Steel Beams use category-specific image URLs, not shared floor plan PDFs.
    const isImageCategory = (id: string) =>
      id === CATEGORY.HVAC.id || id === CATEGORY.COLUMNS.id || id === CATEGORY.STEEL_BEAMS.id;

    // Build canonical URL map: first non-image category with a URL per floor wins.
    const urlMap = new Map<string, string>();
    for (const result of this.categoryResults()) {
      if (isImageCategory(result.categoryId)) continue;
      for (const floor of result.floors ?? []) {
        if (floor.croppedPdfUrl && !urlMap.has(slug(floor.floorName))) {
          urlMap.set(slug(floor.floorName), floor.croppedPdfUrl);
        }
      }
    }
    if (!urlMap.size) return;

    // Apply canonical URL to all non-image categories — even those that already have a URL.
    // This ensures flooring, drywall, and IP all use the same PDF file per floor, so switching
    // tabs between them hits the "same URL" fast path and avoids re-rendering the PDF.
    this.categoryResults.update(results =>
      results.map(r => isImageCategory(r.categoryId) ? r : {
        ...r,
        floors: (r.floors ?? []).map(f => ({
          ...f,
          croppedPdfUrl: urlMap.get(slug(f.floorName)) ?? f.croppedPdfUrl,
        })),
      }),
    );
  }

  // ── Server hydration ──────────────────────────────────────────────────────────

  private tryHydrateFromServer(): void {
    const projectId  = this.project()?.projectId;
    const categories = this.selectedCategories();
    if (!projectId) return;

    this.hydrationLoading.set(true);

    const hasDw       = categories.includes(CATEGORY.DOORS_WINDOWS.id);
    const hasFlooring = categories.includes(CATEGORY.FLOORING.id);
    const hasDrywall  = categories.includes(CATEGORY.DRYWALL.id);
    const hasPlumbing = categories.includes(CATEGORY.FIXTURES.id);
    const hasPaint    = categories.includes(CATEGORY.INTERIOR_PAINT.id);
    const hasHvac     = categories.includes(CATEGORY.HVAC.id);
    const hasColumns  = categories.includes(CATEGORY.COLUMNS.id);
    const hasBeams    = categories.includes(CATEGORY.STEEL_BEAMS.id);

    const fetchPoints = (fileTypeId: number) =>
      this.projectService.getPointsData(projectId, fileTypeId).pipe(
        map(res => res.data),
        catchError(() => of(null as PointsDataResponse | null)),
      );

    forkJoin({
      documents:    this.projectService.getProjectDocuments(projectId).pipe(catchError(() => of(null))),
      floors:       this.projectService.getAssignedFloors(projectId).pipe(catchError(() => of(null))),
      flooring:     hasFlooring ? fetchPoints(CATEGORY.FLOORING.numericId)       : of(null),
      drywall:      hasDrywall  ? fetchPoints(CATEGORY.DRYWALL.numericId)        : of(null),
      plumbing:     hasPlumbing ? fetchPoints(CATEGORY.FIXTURES.numericId)       : of(null),
      paint:        hasPaint    ? fetchPoints(CATEGORY.INTERIOR_PAINT.numericId) : of(null),
      hvac:         hasHvac     ? fetchPoints(CATEGORY.HVAC.numericId)           : of(null),
      columns:      hasColumns  ? fetchPoints(CATEGORY.COLUMNS.numericId)        : of(null),
      beams:        hasBeams    ? fetchPoints(CATEGORY.STEEL_BEAMS.numericId)    : of(null),
      doorsWindows: hasDw ? this.doorsWindowsApi.getPoints(projectId).pipe(catchError(() => of(null))) : of(null),
    }).pipe(
      finalize(() => this.hydrationLoading.set(false)),
    ).subscribe(({ documents, floors, flooring, drywall, plumbing, paint, hvac, columns, beams, doorsWindows }) => {
      if (this.newDocumentUploaded()) return;

      if (documents?.data) {
        this.existingDocuments.set(documents.data.documents.main);
        this.hydrateSelectedScale(documents.data.selectedScale);
        this.documentsLoaded.set(true);
      }

      if (floors) {
        const scale = this.floorService.hydrateFromEntries(floors.data.assigned_floors);
        this.hydrateSelectedScale(scale);
        this.floorService.step3DataLoaded.set(true);
      }

      const hydratedCategories = this.selectedCategories();
      if (!hydratedCategories.length) return;

      const flooringFloors = flooring ? this.extractDetectedFloors(flooring, CATEGORY.FLOORING.numericId) : [];
      const plumbingFloors = plumbing ? this.extractDetectedFloors(plumbing, CATEGORY.FIXTURES.numericId) : [];
      const hvacFloors     = hvac     ? this.extractDetectedFloors(hvac,     CATEGORY.HVAC.numericId)     : [];
      const columnsFloors  = columns  ? this.extractDetectedFloors(columns,  CATEGORY.COLUMNS.numericId)  : [];
      const beamsFloors    = beams    ? this.extractDetectedFloors(beams,    CATEGORY.STEEL_BEAMS.numericId) : [];
      const dwHasData = Object.keys(doorsWindows?.data?.doors_windows_points_data?.floors ?? {}).length > 0;

      // basePolygonFloors provides the canonical floor structure (names + PDF URLs) used as a
      // fallback for room-based categories with no data of their own. Plumbing floors contain
      // fixture polygons, not rooms, so they must never supply room data to other categories —
      // strip rooms from plumbing floors before using them as the base.
      const basePolygonFloors = flooringFloors.length
        ? flooringFloors
        : plumbingFloors.map(f => ({ ...f, rooms: [], totalRooms: 0 }));

      if (!basePolygonFloors.length && !dwHasData && !hvacFloors.length && !columnsFloors.length && !beamsFloors.length) return;

      this.initCategoryResults();

      if (basePolygonFloors.length || hvacFloors.length || columnsFloors.length || beamsFloors.length) {
        const extractedMap: Record<string, DetectedFloorData[]> = {
          [CATEGORY.FLOORING.id]:       flooringFloors,
          [CATEGORY.DRYWALL.id]:        drywall ? this.extractDetectedFloors(drywall, CATEGORY.DRYWALL.numericId) : [],
          [CATEGORY.FIXTURES.id]:       plumbingFloors,
          [CATEGORY.INTERIOR_PAINT.id]: paint   ? this.extractDetectedFloors(paint, CATEGORY.INTERIOR_PAINT.numericId) : [],
          [CATEGORY.HVAC.id]:           hvacFloors,
          [CATEGORY.COLUMNS.id]:        columnsFloors,
          [CATEGORY.STEEL_BEAMS.id]:    beamsFloors,
        };

        const pdfUrlByFloorName = new Map(
          [...plumbingFloors, ...flooringFloors].map(f => [f.floorName, f.croppedPdfUrl]),
        );

        hydratedCategories
          .filter(catId => catId !== CATEGORY.DOORS_WINDOWS.id)
          .forEach(catId => {
            const extracted = extractedMap[catId] ?? [];
            // If a category has no data of its own, leave it idle so the
            // ngOnInit idle handler can re-fetch it from the server.
            if (!extracted.length) return;
            const categoryFloors = extracted.map(f => ({
              ...f,
              croppedPdfUrl: f.croppedPdfUrl ?? pdfUrlByFloorName.get(f.floorName),
            }));
            this.setCategoryComplete(catId, categoryFloors);
          });

        this.propagatePdfUrls();
        Object.entries(extractedMap)
          .filter(([catId, floors]) => hydratedCategories.includes(catId) && floors.length > 0)
          .forEach(([catId]) => this.markCategoryPreProcessComplete(catId));
      }

      this.currentStep.set(4);
    });
  }

  // ── Data extraction ───────────────────────────────────────────────────────────

  extractDetectedFloors(data: PointsDataResponse, fileTypeId?: number): DetectedFloorData[] {
    const all = data.contour_points ?? [];
    const floorEntries = all.filter(entry => entry.is_floor);
    const source = floorEntries.length > 0
      ? floorEntries
      : all.filter(entry => !entry.is_elevation && !entry.is_foundation);

    const isFixtures      = fileTypeId === CATEGORY.FIXTURES.numericId;
    const isHvac          = fileTypeId === CATEGORY.HVAC.numericId;
    const isColumns       = fileTypeId === CATEGORY.COLUMNS.numericId;
    const isSteelBeams    = fileTypeId === CATEGORY.STEEL_BEAMS.numericId;
    const isInteriorPaint = fileTypeId === CATEGORY.INTERIOR_PAINT.numericId;
    const measurementsFloors = (isColumns || isSteelBeams)
      ? this.extractStructuralMeasurementFloors(data, isColumns ? 'columns' : 'beams')
      : [];
    if (measurementsFloors.length) return measurementsFloors;

    const mapped = source.map((entry) => {
      const floorNameSlug = entry.floor_name.toLowerCase().replace(/ /g, '_');
      let rooms: DetectedRoomData[];
      let roomContext: { name: string; points: [number, number][] }[] | undefined;

      if (isFixtures) {
        const modelsContours: any[] = (entry.points_data as any)?.models?.plumbing?.contours ?? [];
        const flatContours: any[]   = (entry.points_data as any)?.contours ?? [];
        const fixtures: any[]       = (modelsContours.length ? modelsContours : flatContours)
          .filter((c) => this.isPlumbingFixtureContour(c));
        const roomContours: any[]   = this.extractRoomContextContours(entry.points_data);
        rooms = fixtures.map((c) => {
          const pts: [number, number][] = c.points ?? [];
          const cx = pts.length ? pts.reduce((s, p) => s + p[0], 0) / pts.length : 0;
          const cy = pts.length ? pts.reduce((s, p) => s + p[1], 0) / pts.length : 0;
          const containingRoom = roomContours.length
            ? roomContours.find((r) => this.pointInPolygon([cx, cy], r.points ?? []))
            : null;
          return {
            name:      c.class_name ?? c.Room_Name ?? 'Fixture',
            points:    pts,
            roomId:    c.class_id ?? c.Room_ID,
            area:      c.area,
            perimeter: c.length ?? c.perimeter,
            corners:   c.corners,
            holes:     c.holes ?? [],
            text:      c.class_name ? [c.class_name] : (c.Room_Name ? [c.Room_Name] : []),
            roomName:  containingRoom?.Room_Name ?? c.Room_Name ?? c.room_name,
          };
        });
        if (roomContours.length) {
          roomContext = roomContours
            .map((r) => ({
              name:       this.roomContourName(r),
              points:     (r.points ?? []) as [number, number][],
              perimeterM: typeof r.perimeter === 'number' ? r.perimeter : undefined,
            }))
            .filter((r) => r.name && r.points.length > 0);
        }
      } else if (isHvac) {
        const hvacContours: any[] = (entry.points_data as any)?.models?.hvac?.contours ?? [];
        const flatContours: any[] = (entry.points_data as any)?.contours ?? [];
        const responseFields: any[] =
          (entry.points_data as any)?.generated_response?.fields ??
          (entry.points_data as any)?.fields ??
          (entry as any)?.generated_response?.fields ??
          [];
        const measuredDucts = responseFields.filter((field) =>
          Array.isArray(field?.value) &&
          (field?.measured_length_in != null || field?.measured_width_in != null),
        );
        const fromSavedFlat      = flatContours.length > 0;
        const fromAI             = !fromSavedFlat && hvacContours.length > 0;
        const fromMeasuredFields = !fromSavedFlat && !fromAI && measuredDucts.length > 0;
        const hvacSource = fromSavedFlat ? flatContours : fromAI ? hvacContours : measuredDucts;
        rooms = hvacSource.map((c) => {
          const rawLen = typeof c.measured_length_in === 'number' ? c.measured_length_in
                       : typeof c.length             === 'number' ? c.length
                       : typeof c.area               === 'number' ? c.area
                       : parseFloat(c.measured_length_in) || parseFloat(c.length) || parseFloat(c.area) || 0;
          const rawThk = typeof c.measured_width_in === 'number' ? c.measured_width_in
                       : typeof c.thickness         === 'number' ? c.thickness
                       : typeof c.perimeter         === 'number' ? c.perimeter
                       : parseFloat(c.measured_width_in) || parseFloat(c.thickness) || parseFloat(c.perimeter) || 0;
          return {
            name:      c.class_name ?? c.Room_Name ?? c.name ?? 'Duct',
            points:    (c.points ?? c.value ?? []) as [number, number][],
            roomId:    c.class_id ?? c.Room_ID,
            area:      (fromAI || fromMeasuredFields) ? rawLen / 12 : rawLen,
            perimeter: rawThk,
            corners:   ((c.points ?? c.value) as unknown[])?.length ?? 0,
            holes:     [],
            text:      [],
          };
        });
      } else if (isColumns) {
        const columnsContours: any[] =
          (entry.points_data as any)?.models?.columns?.contours ??
          (entry.points_data as any)?.models?.column?.contours ??
          (entry.points_data as any)?.models?.structural_columns?.contours ??
          [];
        const flatContours: any[]   = (entry.points_data as any)?.contours ?? [];
        const responseFields: any[] =
          (entry.points_data as any)?.generated_response?.fields ??
          (entry.points_data as any)?.fields ??
          (entry as any)?.generated_response?.fields ??
          [];
        const measuredColumns = responseFields.filter((field) =>
          Array.isArray(field?.value ?? field?.points) &&
          (field?.measured_length_in != null || field?.measured_width_in != null || field?.column_type != null),
        );
        const src = columnsContours.length ? columnsContours : flatContours.length ? flatContours : measuredColumns;
        rooms = src.map((c, index) => {
          const rawLength = typeof c.measured_length_in === 'number' ? c.measured_length_in
                          : typeof c.length             === 'number' ? c.length
                          : typeof c.area               === 'number' ? c.area
                          : parseFloat(c.measured_length_in) || parseFloat(c.length) || parseFloat(c.area) || 0;
          const rawWidth = typeof c.measured_width_in === 'number' ? c.measured_width_in
                         : typeof c.width             === 'number' ? c.width
                         : typeof c.thickness         === 'number' ? c.thickness
                         : typeof c.perimeter         === 'number' ? c.perimeter
                         : parseFloat(c.measured_width_in) || parseFloat(c.width) || parseFloat(c.thickness) || parseFloat(c.perimeter) || 0;
          const points = (c.points ?? c.value ?? []) as [number, number][];
          const columnType = c.column_type ?? c.type ?? c.item_type ?? c.class_name ?? '';
          return {
            name:      (c.name ?? c.class_name ?? c.label ?? `Column ${index + 1}`).replace(/:$/, '').trim(),
            points,
            roomId:    c.class_id ?? c.Room_ID,
            area:      rawLength,
            perimeter: rawWidth,
            corners:   points.length,
            holes:     [],
            text:      [columnType, columnType],
          };
        });
      } else if (isSteelBeams) {
        const beamsContours: any[] = (entry.points_data as any)?.models?.beams?.contours ?? [];
        const flatContours: any[]  = (entry.points_data as any)?.contours ?? [];
        const src = beamsContours.length ? beamsContours : flatContours;
        rooms = src.map((c) => ({
          name:      (c.name ?? c.class_name ?? 'Beam').replace(/:$/, '').trim(),
          points:    (c.points ?? []) as [number, number][],
          roomId:    c.class_id,
          area:      typeof c.length    === 'number' ? c.length    : parseFloat(c.length)    || 0,
          perimeter: typeof c.thickness === 'number' ? c.thickness : parseFloat(c.thickness) || 0,
          corners:   (c.points as unknown[])?.length ?? 0,
          holes:     [],
          text:      [c.beam_type ?? '', c.beam_type ?? ''],
        }));
      } else if (isInteriorPaint) {
        // Interior paint API returns rooms under points_data.room_and_area.contours.
        // Falls back to legacy flat contours and models.rooms for older responses.
        const roomAndAreaContours: any[] = (entry.points_data as any)?.room_and_area?.contours ?? [];
        const flatContours: any[] = (entry.points_data as any)?.contours ?? [];
        const roomsContours: any[] = (entry.points_data as any)?.models?.rooms?.contours ?? [];
        const src: any[] = roomAndAreaContours.length ? roomAndAreaContours
          : flatContours.length ? flatContours
          : roomsContours;
        rooms = src.map((c) => ({
          name:      c.Room_Name ?? c.room_name ?? c.class_name ?? c.name,
          points:    c.points,
          roomId:    c.Room_ID ?? c.room_id ?? c.class_id,
          area:      c.area,
          perimeter: c.perimeter,
          corners:   c.corners,
          holes:     c.holes ?? [],
          text:      c.text,
        }));
      } else {
        const rawContours: unknown[] =
          (entry.points_data as any)?.contours ??
          (entry.points_data as any)?.models?.rooms?.contours ??
          [];
        rooms = (rawContours as any[]).map((c) => ({
          name:      c.Room_Name,
          points:    c.points,
          roomId:    c.Room_ID,
          area:      c.area,
          perimeter: c.perimeter,
          corners:   c.corners,
          holes:     c.holes,
          text:      c.text,
        }));
      }

      const gptMetrics = entry.points_data.gpt_wall_metrics;
      const externalWallContours: unknown[] =
        entry.points_data.models?.external_wall?.contours ??
        (entry.points_data as any)?.external_wall?.contours ??
        [];
      const externalWallMetrics = calculateExternalWallMetrics(externalWallContours);

      // Prefer GPT-measured values (already in ft) over pixel-derived calculations.
      const perimeterFt = gptMetrics?.total_external_walls_ft     || externalWallMetrics.perimeterFt;
      const longestFt   = gptMetrics?.longest_external_wall_ft     || externalWallMetrics.longestFt;
      const perpFt      = gptMetrics?.perpendicular_external_wall_ft != null
        ? gptMetrics.perpendicular_external_wall_ft
        : externalWallMetrics.perpendicularFt;

      return {
        floorName:     entry.floor_name,
        floorNumber:   entry.floor_number,
        id:            entry.id,
        rooms,
        croppedPdfUrl: this.findCroppedPdfUrl(data.cropped_images ?? [], floorNameSlug)
          ?? ((isColumns || isSteelBeams) ? data.cropped_images?.[0]?.url : undefined),
        totalRooms:    rooms.length,
        ...(roomContext ? { roomContext } : {}),
        ...(externalWallMetrics.walls.length ? { externalWalls: externalWallMetrics.walls } : {}),
        ...(perimeterFt ? { externalWallPerimeterFt:     perimeterFt } : {}),
        ...(longestFt   ? { longestExternalWallFt:       longestFt   } : {}),
        ...(perpFt      ? { perpendicularExternalWallFt: perpFt      } : {}),
      };
    });

    // Deduplicate floors that share the same normalized slug.
    // Prefer the natural-name entry (spaces in floor_name) over the slug-only entry (underscores).
    const mergeRoomContext = (primary: DetectedFloorData, secondary: DetectedFloorData): DetectedFloorData => {
      const roomContext = [...(primary.roomContext ?? [])];
      const seenRooms = new Set(roomContext.map(r => r.name.toLowerCase()));
      for (const room of secondary.roomContext ?? []) {
        const key = room.name.toLowerCase();
        if (!seenRooms.has(key)) { roomContext.push(room); seenRooms.add(key); }
      }
      const externalWalls = primary.externalWalls?.length ? primary.externalWalls : secondary.externalWalls;
      return {
        ...primary,
        ...(roomContext.length ? { roomContext } : {}),
        ...(externalWalls?.length ? { externalWalls } : {}),
        externalWallPerimeterFt:     primary.externalWallPerimeterFt     ?? secondary.externalWallPerimeterFt,
        longestExternalWallFt:       primary.longestExternalWallFt       ?? secondary.longestExternalWallFt,
        perpendicularExternalWallFt: primary.perpendicularExternalWallFt ?? secondary.perpendicularExternalWallFt,
      };
    };

    const seen = new Map<string, DetectedFloorData>();
    for (const floor of mapped) {
      const slug = floor.floorName.toLowerCase().replace(/[_ ]+/g, '_');
      const existing = seen.get(slug);
      const newIsNatural      = floor.floorName.includes(' ');
      const existingIsNatural = existing?.floorName.includes(' ') ?? false;
      if (!existing || floor.rooms.length > existing.rooms.length || (newIsNatural && !existingIsNatural)) {
        seen.set(slug, existing ? mergeRoomContext(floor, existing) : floor);
      } else {
        seen.set(slug, mergeRoomContext(existing, floor));
      }
    }

    return [...seen.values()].sort((a, b) => floorSortIndex(a.floorName) - floorSortIndex(b.floorName));
  }

  private findCroppedPdfUrl(images: CroppedImage[], floorNameSlug: string): string | undefined {
    return images
      .map(img => img.url ?? img.pdf_url)
      .find(url => url?.toLowerCase().includes(floorNameSlug));
  }

  private extractRoomContextContours(pointsData: unknown): any[] {
    const data = pointsData as any;
    const nested = [
      ...(data?.models?.rooms?.contours ?? []),
      ...(data?.models?.flooring?.contours ?? []),
      ...(data?.rooms?.contours ?? []),
      ...(data?.flooring?.contours ?? []),
      ...(data?.room_contours ?? []),
    ];
    const flatRooms = (data?.contours ?? []).filter((contour: any) =>
      !this.isPlumbingFixtureContour(contour) && this.roomContourName(contour) && Array.isArray(contour?.points),
    );
    return [...nested, ...flatRooms].filter((contour: any) =>
      this.roomContourName(contour) && Array.isArray(contour?.points),
    );
  }

  private roomContourName(contour: any): string {
    return String(
      contour?.Room_Name ?? contour?.room_name ?? contour?.name ?? contour?.label ?? '',
    ).trim();
  }

  private extractStructuralMeasurementFloors(
    data: PointsDataResponse,
    kind: 'columns' | 'beams',
  ): DetectedFloorData[] {
    const measurements: any[] = (data as any)?.all_measurements ?? [];
    if (!measurements.length) return [];

    return measurements
      .map((measurement) => {
        const floorNumber = Number(measurement.floor_number ?? 1);
        const contourFloor = (data.contour_points ?? []).find(
          entry => Number(entry.floor_number) === floorNumber,
        );
        const fieldKey     = kind === 'columns' ? 'Matched_Columns' : 'Matched_Beams';
        const typeKey      = kind === 'columns' ? 'Column_Type'     : 'Beam_Type';
        const fallbackLabel = kind === 'columns' ? 'Column' : 'Beam';
        const rooms = ((measurement.generated_response?.fields ?? []) as any[]).flatMap((field) => {
          const structuralType = field?.[typeKey] ?? '';
          return ((field?.[fieldKey] ?? []) as any[]).map((item, index) => {
            const points = (item.points ?? item.value ?? []) as [number, number][];
            const length = typeof item.measured_length_in === 'number'
              ? item.measured_length_in
              : parseFloat(item.measured_length_in) || parseFloat(item.length) || 0;
            const width = typeof item.measured_width_in === 'number'
              ? item.measured_width_in
              : parseFloat(item.measured_width_in) || parseFloat(item.thickness) || parseFloat(item.width) || 0;
            return {
              name:      (item.name ?? `${fallbackLabel} ${index + 1}`).replace(/:$/, '').trim(),
              points,
              roomId:    item.class_id ?? item.Room_ID,
              area:      length,
              perimeter: width,
              corners:   points.length,
              holes:     [],
              text:      [structuralType, structuralType],
            };
          });
        });

        const croppedPdfUrl = (contourFloor
          ? this.findCroppedPdfUrl(data.cropped_images ?? [], contourFloor.floor_name.toLowerCase().replace(/ /g, '_'))
          : undefined)
          ?? data.cropped_images?.[0]?.url
          ?? measurement.pdf_url;

        return {
          floorName:   contourFloor?.floor_name ?? measurement.floor_name ?? `${fallbackLabel}s Floor ${floorNumber}`,
          floorNumber,
          id:          measurement.id ?? contourFloor?.id ?? 0,
          rooms,
          croppedPdfUrl,
          totalRooms:  rooms.length,
        };
      })
      .filter(floor => floor.rooms.length > 0);
  }

  private isPlumbingFixtureContour(contour: any): boolean {
    return Boolean(contour?.class_name || contour?.class_id || contour?.fixture_name || contour?.fixture_id);
  }

  private pointInPolygon(point: [number, number], polygon: [number, number][]): boolean {
    const [px, py] = point;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }
}
